import { authFetch } from '@/utils/api';
import { saveBlob } from './download';

/**
 * API client of the program "Arquitetura de Operação" (/api/programa).
 * Plan: docs/plans/PLAN_arquitetura-de-operacao.md
 */

export const PROGRAM_NAME = 'Arquitetura de Operação';

export const programaKeys = {
  me: () => ['programa-me'],
  cohort: (cohortId) => ['programa-cohort', cohortId],
  accounts: (q) => ['programa-accounts', q],
  home: (officeId) => ['programa-office-home', officeId],
  onboarding: (officeId) => ['programa-office-onboarding', officeId],
  dashboard: (officeId) => ['programa-office-dashboard', officeId],
  latestImport: (officeId, kind = 'attendance') => ['programa-office-latest-import', officeId, kind],
  revenue: (officeId) => ['programa-office-revenue', officeId],
  configs: (officeId) => ['programa-office-configs', officeId],
  leads: (officeId) => ['programa-office-leads', officeId],
  consultations: (officeId) => ['programa-office-consultations', officeId],
  sales: (officeId) => ['programa-office-sales', officeId],
  reactivation: (officeId) => ['programa-office-reactivation', officeId],
  questions: (officeId) => ['programa-office-questions', officeId],
  heatmap: (officeId) => ['programa-office-heatmap', officeId],
  automationMetric: (officeId) => ['programa-office-automation-metric', officeId],
  audit: (officeId, period = 'latest') => ['programa-office-audit', officeId, period],
  mysteryShop: (cohortId) => ['programa-mystery-shop', cohortId],
  officeMysteryShop: (officeId) => ['programa-office-mystery-shop', officeId],
  sprint: (officeId) => ['programa-office-sprint', officeId],
  scoreboard: (cohortId) => ['programa-scoreboard', cohortId],
  central: (cohortId) => ['programa-central', cohortId],
  dispatches: (cohortId) => ['programa-dispatches', cohortId],
  week: (officeId, code) => ['programa-office-week', officeId, code],
  manual: (officeId) => ['programa-office-manual', officeId],
  revisions: (officeId, chapterKey) => ['programa-chapter-revisions', officeId, chapterKey],
  lessons: (cohortId, filters) => ['programa-lessons', cohortId, filters],
  banca: (meetingId) => ['programa-banca', meetingId],
  content: (cohortId) => ['programa-content', cohortId ?? 'program'],
  contentVersions: (cohortId, kind, key) => ['programa-content-versions', cohortId ?? 'program', kind, key],
};

const BASE = '/api/programa';

/** Throws with the API message, `code` and `details` — screens show them as-is. */
export async function programaRequest(path, options = {}) {
  // apiFetch sets JSON headers (and drops them for FormData) and the cookie.
  const response = await authFetch(`${BASE}${path}`, options);
  const data = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    throw Object.assign(new Error(data?.error || 'Não foi possível falar com o programa. Tente novamente.'), {
      code: data?.code,
      status: response.status,
      details: data?.details,
    });
  }
  return data?.data ?? data;
}

export const programaApi = {
  me: () => programaRequest('/me'),
  searchAccounts: (q) => programaRequest(`/accounts?q=${encodeURIComponent(q)}`),
  createCohort: (body) => programaRequest('/cohorts', { method: 'POST', body: JSON.stringify(body) }),
  cohort: (cohortId) => programaRequest(`/cohorts/${cohortId}`),
  updateCohort: (cohortId, body) => programaRequest(`/cohorts/${cohortId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  updateWeek: (cohortId, weekId, body) => programaRequest(`/cohorts/${cohortId}/weeks/${weekId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  updateMeeting: (cohortId, meetingId, body) =>
    programaRequest(`/cohorts/${cohortId}/meetings/${meetingId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  registerOffice: (cohortId, body) => programaRequest(`/cohorts/${cohortId}/offices`, { method: 'POST', body: JSON.stringify(body) }),
  addStaff: (cohortId, body) => programaRequest(`/cohorts/${cohortId}/participants`, { method: 'POST', body: JSON.stringify(body) }),
  updateStaff: (cohortId, participantId, body) =>
    programaRequest(`/cohorts/${cohortId}/participants/${participantId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  home: (officeId) => programaRequest(`/offices/${officeId}/home`),
  onboarding: (officeId) => programaRequest(`/offices/${officeId}/onboarding`),
  updateOnboarding: (officeId, body) => programaRequest(`/offices/${officeId}/onboarding`, { method: 'PATCH', body: JSON.stringify(body) }),
  acceptConsent: (officeId, body) => programaRequest(`/offices/${officeId}/consents`, { method: 'POST', body: JSON.stringify(body) }),
  revokeConsent: (officeId, type) => programaRequest(`/offices/${officeId}/consents/${type}/revoke`, { method: 'POST' }),
  addMember: (officeId, body) => programaRequest(`/offices/${officeId}/participants`, { method: 'POST', body: JSON.stringify(body) }),
  updateMember: (officeId, participantId, body) =>
    programaRequest(`/offices/${officeId}/participants/${participantId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  dashboard: (officeId) => programaRequest(`/offices/${officeId}/dashboard`),
  refreshDashboard: (officeId, body = {}) => programaRequest(`/offices/${officeId}/dashboard/refresh`, { method: 'POST', body: JSON.stringify(body) }),
  latestImport: (officeId, kind = 'attendance') => programaRequest(`/offices/${officeId}/imports/latest?kind=${kind}`),
  importCsv: (officeId, body, kind = 'attendance') =>
    programaRequest(`/offices/${officeId}/imports?kind=${kind}`, { method: 'POST', body: JSON.stringify(body) }),
  revenue: (officeId) => programaRequest(`/offices/${officeId}/revenue`),
  configs: (officeId) => programaRequest(`/offices/${officeId}/configs`),
  leads: (officeId) => programaRequest(`/offices/${officeId}/leads`),
  consultations: (officeId) => programaRequest(`/offices/${officeId}/consultations`),
  sales: (officeId) => programaRequest(`/offices/${officeId}/sales`),
  reactivation: (officeId) => programaRequest(`/offices/${officeId}/reactivation`),
  questions: (officeId) => programaRequest(`/offices/${officeId}/questions`),
  importQuestions: (officeId, body) => programaRequest(`/offices/${officeId}/questions/import`, { method: 'POST', body: JSON.stringify(body) }),
  reviewQuestion: (officeId, questionId, body) => programaRequest(`/offices/${officeId}/questions/${questionId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  heatmap: (officeId) => programaRequest(`/offices/${officeId}/heatmap`),
  automationMetric: (officeId) => programaRequest(`/offices/${officeId}/automation/metric`),
  async downloadHeatmap(officeId) {
    const response = await authFetch(`${BASE}/offices/${officeId}/heatmap?format=csv`);
    if (!response.ok) throw new Error('Não foi possível gerar o arquivo. Tente novamente.');
    const name = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') || '')?.[1] || 'atendimentos-por-dia-e-hora.csv';
    saveBlob(await response.blob(), name);
  },
  audit: (officeId, period) => programaRequest(`/offices/${officeId}/audits${period ? `?period=${encodeURIComponent(period)}` : ''}`),
  drawAudit: (officeId) => programaRequest(`/offices/${officeId}/audits`, { method: 'POST' }),
  reviewAuditItem: (officeId, itemId, body) => programaRequest(`/offices/${officeId}/audit-items/${itemId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  addDoubt: (officeId, text) => programaRequest(`/offices/${officeId}/questions/doubts`, { method: 'POST', body: JSON.stringify({ text }) }),
  reactivationManual: (officeId, body) => programaRequest(`/offices/${officeId}/reactivation/manual`, { method: 'POST', body: JSON.stringify(body) }),
  addConsultation: (officeId, body) => programaRequest(`/offices/${officeId}/consultations`, { method: 'POST', body: JSON.stringify(body) }),
  markConsultation: (officeId, consultationId, body) =>
    programaRequest(`/offices/${officeId}/consultations/${consultationId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  mysteryShop: (cohortId) => programaRequest(`/cohorts/${cohortId}/mystery-shop`),
  drawMysteryShop: (cohortId, body) => programaRequest(`/cohorts/${cohortId}/mystery-shop/draw`, { method: 'POST', body: JSON.stringify(body) }),
  mysteryShopSent: (cohortId, shopId) => programaRequest(`/cohorts/${cohortId}/mystery-shop/${shopId}/sent`, { method: 'POST', body: '{}' }),
  mysteryShopReply: (cohortId, shopId, form) => programaRequest(`/cohorts/${cohortId}/mystery-shop/${shopId}/reply`, { method: 'POST', body: form }),
  officeMysteryShop: (officeId) => programaRequest(`/offices/${officeId}/mystery-shop`),
  setConfigActive: (officeId, configId, action, payload) =>
    programaRequest(`/offices/${officeId}/configs/${configId}/${action}`, { method: 'POST', ...(payload ? { body: JSON.stringify({ payload }) } : {}) }),
  uploadEvidence: (officeId, file) => {
    const form = new FormData();
    form.append('file', file);
    return programaRequest(`/offices/${officeId}/evidence`, { method: 'POST', body: form });
  },
  openBoard: (meetingId) => programaRequest(`/meetings/${meetingId}/board`, { method: 'POST' }),
  closeBoard: (sessionId) => programaRequest(`/boards/${sessionId}/close`, { method: 'POST' }),
  sprint: (officeId) => programaRequest(`/offices/${officeId}/sprint`),
  saveCard: (officeId, body) => programaRequest(`/offices/${officeId}/sprint/card`, { method: 'PUT', body: JSON.stringify(body) }),
  submitRetro: (officeId, body) => programaRequest(`/offices/${officeId}/sprint/retro`, { method: 'POST', body: JSON.stringify(body) }),
  reportAbsence: (officeId, body) => programaRequest(`/offices/${officeId}/absence`, { method: 'POST', body: JSON.stringify(body) }),
  scoreboard: (cohortId) => programaRequest(`/cohorts/${cohortId}/scoreboard`),
  central: (cohortId) => programaRequest(`/cohorts/${cohortId}/central`),
  week: (officeId, code) => programaRequest(`/offices/${officeId}/weeks/${code}`),
  saveDeliverable: (officeId, templateKey, body) =>
    programaRequest(`/offices/${officeId}/deliverables/${templateKey}`, { method: 'PUT', body: JSON.stringify(body) }),
  prefill: (officeId, templateKey) => programaRequest(`/offices/${officeId}/deliverables/${templateKey}/prefill`),
  submitDeliverable: (officeId, templateKey) => programaRequest(`/offices/${officeId}/deliverables/${templateKey}/submit`, { method: 'POST' }),
  approveDeliverable: (officeId, templateKey) => programaRequest(`/offices/${officeId}/deliverables/${templateKey}/approve`, { method: 'POST' }),
  reopenDeliverable: (officeId, templateKey) => programaRequest(`/offices/${officeId}/deliverables/${templateKey}/reopen`, { method: 'POST' }),
  addComment: (officeId, templateKey, body) =>
    programaRequest(`/offices/${officeId}/deliverables/${templateKey}/comments`, { method: 'POST', body: JSON.stringify({ body }) }),
  manual: (officeId) => programaRequest(`/offices/${officeId}/manual`),
  saveChapter: (officeId, chapterKey, body) =>
    programaRequest(`/offices/${officeId}/manual/chapters/${chapterKey}`, { method: 'PUT', body: JSON.stringify(body) }),
  revisions: (officeId, chapterKey) => programaRequest(`/offices/${officeId}/manual/chapters/${chapterKey}/revisions`),
  /** Downloads the Manual (DOCX or PDF) as a file. */
  async downloadManual(officeId, format) {
    const response = await authFetch(`${BASE}/offices/${officeId}/manual/export?format=${format}`);
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.error || 'Não foi possível gerar o arquivo. Tente novamente.');
    }
    const name = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') || '')?.[1] || `manual-de-operacao.${format}`;
    saveBlob(await response.blob(), name);
  },
  /** Downloads the approved régua (S7): the .twflow to import, or the CSV of the touches. */
  /** Files of a generated configuration: `regua` (twflow|csv) or `agent-base` (kb|handoff). */
  async downloadConfigFile(officeId, resource, format) {
    const response = await authFetch(`${BASE}/offices/${officeId}/${resource}/export?format=${format}`);
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.error || 'Não foi possível gerar o arquivo. Tente novamente.');
    }
    const name = /filename="([^"]+)"/.exec(response.headers.get('content-disposition') || '')?.[1] || `${resource}.${format === 'twflow' ? 'twflow.json' : 'csv'}`;
    saveBlob(await response.blob(), name);
  },
  drawRotation: (cohortId, volunteerOfficeId) =>
    programaRequest(`/cohorts/${cohortId}/rotation/draw`, { method: 'POST', body: JSON.stringify({ volunteerOfficeId }) }),
  generateRotation: (cohortId) => programaRequest(`/cohorts/${cohortId}/rotation/generate`, { method: 'POST' }),
  skipHotseat: (meetingId) => programaRequest(`/meetings/${meetingId}/hotseat/skip`, { method: 'POST' }),
  dispatches: (cohortId) => programaRequest(`/cohorts/${cohortId}/dispatches`),
  lessons: (cohortId, filters = {}) => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    return programaRequest(`/cohorts/${cohortId}/lessons${params.size ? `?${params}` : ''}`);
  },
  markLesson: (lessonId, body) => programaRequest(`/lessons/${lessonId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  recordMeasurement: (officeId, body) => programaRequest(`/offices/${officeId}/measurements`, { method: 'POST', body: JSON.stringify(body) }),
  // Banca (after S12)
  scheduleBanca: (cohortId, body) => programaRequest(`/cohorts/${cohortId}/banca`, { method: 'PUT', body: JSON.stringify(body) }),
  banca: (meetingId) => programaRequest(`/meetings/${meetingId}/banca`),
  scoreRubric: (meetingId, body) => programaRequest(`/meetings/${meetingId}/banca/scores`, { method: 'PUT', body: JSON.stringify(body) }),
  revealRubric: (meetingId) => programaRequest(`/meetings/${meetingId}/banca/reveal`, { method: 'POST' }),
  // Program content editor (null cohortId = the program base)
  content: (cohortId) => programaRequest(cohortId ? `/cohorts/${cohortId}/content` : '/content'),
  publishContent: (cohortId, kind, key, body) =>
    programaRequest(`${cohortId ? `/cohorts/${cohortId}` : ''}/content/${kind}/${encodeURIComponent(key)}`, { method: 'PUT', body: JSON.stringify(body) }),
  resetContent: (cohortId, kind, key) => programaRequest(`${cohortId ? `/cohorts/${cohortId}` : ''}/content/${kind}/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  contentVersions: (cohortId, kind, key) => programaRequest(`${cohortId ? `/cohorts/${cohortId}` : ''}/content/${kind}/${encodeURIComponent(key)}/versions`),
};
